import type { AxiosInstance } from "axios";
import { EMethod } from "@bc-library/interface";
import { API_MAIN_VERSION, BASE_URL } from "@/config/constantes";
import useAxios from "@/services/api/useAxios";

const resource = "userSessions";

export type AdminFirmSummary = {
  _id: string;
  company: string;
  code?: string;
  isDedicatedServer: boolean;
};

export type FirmActivityRow = {
  firm: AdminFirmSummary;
  totalSessions: number;
  activeSessions: number;
  /** Active sessions of API accounts, kept apart from `activeSessions`. */
  apiSessions: number;
  logins24h: number;
  logins7d: number;
  logins30d: number;
  onlineNow: number;
  uniqueUsers24h: number;
  uniqueUsers7d: number;
  uniqueUsers30d: number;
  uniqueUsersPeriod?: number;
  /** Logged in over the period without a licence (departed, candidate, no resource). */
  unlicencedUsersPeriod?: number;
  lastActivityAt: string | null;
  licencedUsers: number;
  adoptionRate7d: number | null;
  adoptionRate30d: number | null;
  adoptionRatePeriod?: number | null;
  loginsPeriod?: number;
  connected: boolean;
  error?: string;
};

export type ActivityTotals = {
  firms: number;
  activeFirms7d: number;
  activeFirmsPeriod?: number;
  unreachableFirms: number;
  activeSessions: number;
  apiSessions: number;
  onlineNow: number;
  uniqueUsers24h: number;
  uniqueUsers7d: number;
  uniqueUsers30d: number;
  uniqueUsersPeriod?: number;
  unlicencedUsersPeriod?: number;
  logins24h: number;
  logins7d: number;
  logins30d: number;
  loginsPeriod?: number;
  licencedUsers: number;
  adoptionRate7d: number | null;
  adoptionRate30d: number | null;
  adoptionRatePeriod?: number | null;
};

export type ActivityOverviewResponse = {
  generatedAt: string;
  onlineWindowMinutes: number;
  maxHistoryDays: number;
  days?: number;
  totals: ActivityTotals;
  firms: FirmActivityRow[];
};

export type ActivityDailyPoint = {
  date: string;
  logins: number;
  loginsDedicated: number;
  loginsShared: number;
  uniqueUsers: number;
};

export type ActivityHeatmapCell = {
  /** ISO weekday: 1 = Monday … 7 = Sunday */
  weekday: number;
  hour: number;
  logins: number;
};

export type ActivityDevices = {
  deviceType: Record<string, number>;
  browser: Record<string, number>;
  os: Record<string, number>;
  /** Logins by method: `local` or `sso` */
  provider: Record<string, number>;
};

export type ActivityServerType = "shared" | "dedicated";
export type ActivityProvider = "local" | "sso";

/** Page filters, understood by overview / analytics / list (and `provider` by the breakdown). */
export type ActivityFilters = {
  firmIds?: string;
  serverType?: ActivityServerType;
  provider?: ActivityProvider;
};

export type ActivityGranularity = "day" | "week";

/** One firm's share of a module or page. */
export type UsageFirmSplit = {
  firmId: string;
  company: string | null;
  hits: number;
  uniqueUsers: number;
  targets?: number;
};

export type ModuleUsageRow = {
  module: string;
  hits: number;
  firstHits: number;
  uniqueUsers: number;
  firms: number;
  adoptionRate: number | null;
  byFirm?: UsageFirmSplit[];
};

export type ModuleUsagePoint = {
  date: string;
  module: string;
  hits: number;
  firstHits: number;
  uniqueUsers: number;
};

export type ModuleUsageResponse = {
  days: number;
  granularity?: ActivityGranularity;
  maxHistoryDays: number;
  licencedUsers: number;
  modules: ModuleUsageRow[];
  dailySeries?: ModuleUsagePoint[];
};

/** A route pattern (`/evaluation/:evaluationId`), every id folded in. */
export type PageUsageRow = {
  page: string;
  module: string;
  hits: number;
  uniqueUsers: number;
  /** Distinct concrete paths behind the pattern — 0 for a route without params. */
  targets: number;
  firms: number;
  byFirm: UsageFirmSplit[];
};

export type PageUsageResponse = {
  days: number;
  maxHistoryDays: number;
  pages: PageUsageRow[];
};

export type PageTargetRow = {
  path: string;
  firmId: string;
  company: string | null;
  hits: number;
  uniqueUsers: number;
  lastDate: string;
  /** Visits past the per-user daily cap of distinct paths, kept on the pattern. */
  isOverflow: boolean;
};

export type PageTargetsResponse = {
  page: string;
  days: number;
  total: number;
  targets: PageTargetRow[];
};

export type ModuleDetailFirmRow = UsageFirmSplit & {
  firstHits: number;
  licencedUsers: number;
  adoptionRate: number | null;
  lastDate: string;
};

export type ModuleDetailUser = {
  _id: string;
  userId: string;
  firmId: string;
  company: string | null;
  firstname?: string;
  lastname?: string;
  picture?: string;
  hits: number;
  /** Days with at least one visit of the module. */
  days: number;
  lastDate: string;
};

export type ModuleDetailResponse = {
  module: string;
  days: number;
  maxHistoryDays: number;
  byFirm: ModuleDetailFirmRow[];
  topUsers: ModuleDetailUser[];
  pages: PageUsageRow[];
};

export type ActivityAnalyticsResponse = {
  days: number;
  timezone: string;
  granularity?: ActivityGranularity;
  dailySeries: ActivityDailyPoint[];
  heatmap: ActivityHeatmapCell[];
  devices: ActivityDevices;
  clientSplit: { human: number; api: number };
};

export type WeeklyAdoptionPoint = {
  weekStart: string;
  loggedIn: number;
  active: number;
  rate: number | null;
};

export type WeeklyAdoptionSeries = {
  firmId: string;
  company: string;
  points: WeeklyAdoptionPoint[];
  isDedicatedServer?: boolean;
  connected?: boolean;
};

export type WeeklyAdoptionResponse = {
  weeks: number;
  timezone: string;
  weekStarts: string[];
  series: WeeklyAdoptionSeries[];
  maxHistoryDays: number;
};

export type SessionDevice = {
  browser: string;
  os: string;
  deviceType: string;
  isApiClient: boolean;
};

export type AdminSessionItem = {
  _id: string;
  userId: string;
  resourceId?: string;
  _firmId: string;
  firm: { _id: string; company: string; isDedicatedServer: boolean } | null;
  resource?: { _id: string; firstname?: string; lastname?: string; picture?: string };
  ip?: string;
  userAgent?: string;
  provider?: string;
  device: SessionDevice;
  clientType: "human" | "api";
  isActive: boolean;
  isRevoked: boolean;
  createdAt: string;
  lastActivityAt: string;
  expiresAt: string;
};

export type AdminSessionListResponse = {
  items: AdminSessionItem[];
  total: number;
  page: number;
  limit: number;
};

export type BreakdownRow = {
  _id: string | null;
  display?: string | null;
  role?: string | null;
  licencedUsers: number;
  activeUsers: number;
  onlineNow: number;
  logins: number;
  activeSessions: number;
  lastActivityAt: string | null;
  adoptionRate: number | null;
};

export type BreakdownUser = {
  _id: string;
  firstname?: string;
  lastname?: string;
  picture?: string;
  role?: string;
  office?: string | null;
  job?: string | null;
  logins?: number;
  activeSessions?: number;
  lastActivityAt?: string | null;
  userId: string;
};

export type UnlicencedReason = "noResource" | "deleted" | "candidate" | "departed" | "other";

/** Logged in over the period, but outside the licence base. */
export type UnlicencedUser = {
  _id: string;
  userId: string;
  firstname?: string;
  lastname?: string;
  picture?: string;
  email?: string | null;
  role?: string | null;
  reason: UnlicencedReason;
  departureDate?: string | null;
  logins: number;
  lastActivityAt: string;
};

/** A licenced resource and its figures, sent with `withUsers` for the Excel export. */
export type BreakdownExportUser = {
  _id: string;
  userId: string;
  firstname?: string;
  lastname?: string;
  email?: string | null;
  role?: string;
  officeId?: string | null;
  office?: string | null;
  jobId?: string | null;
  job?: string | null;
  departureDate?: string | null;
  logins: number;
  activeSessions: number;
  lastActivityAt: string | null;
  isActiveInPeriod: boolean;
  isOnline: boolean;
};

export type FirmBreakdownResponse = {
  firm: AdminFirmSummary;
  days: number;
  generatedAt: string;
  totals: {
    licencedUsers: number;
    activeUsers: number;
    onlineNow: number;
    logins: number;
    activeSessions: number;
    lastActivityAt: string | null;
    adoptionRate: number | null;
    /** Absent from a dedicated server not updated yet. */
    neverConnected?: number;
    unlicencedUsers?: number;
  };
  byOffice: BreakdownRow[];
  byJob: BreakdownRow[];
  byRole: BreakdownRow[];
  topUsers: BreakdownUser[];
  neverConnected: BreakdownUser[];
  unlicencedUsers?: UnlicencedUser[];
  users?: BreakdownExportUser[];
};

const base = `${BASE_URL}/${API_MAIN_VERSION}/${resource}/admin`;

const listFirms = (axios: any) => () =>
  axios({
    method: EMethod.get,
    url: `${base}/firms`,
  });

const getOverview =
  (axios: any) =>
  (query: ActivityFilters & { days?: number } = {}) =>
    axios({
      method: EMethod.get,
      url: `${base}/overview`,
      query,
    });

const getAnalytics =
  (axios: any) =>
  (
    query: ActivityFilters & {
      days?: number;
      firmId?: string;
      timezone?: string;
      granularity?: ActivityGranularity;
    },
  ) =>
    axios({
      method: EMethod.get,
      url: `${base}/analytics`,
      query,
    });

const getWeeklyAdoption =
  (axios: any) => (query: ActivityFilters & { weeks?: number; timezone?: string }) =>
    axios({
      method: EMethod.get,
      url: `${base}/weekly-adoption`,
      query,
    });

const getModuleUsage =
  (axios: any) =>
  (query: ActivityFilters & { days?: number; granularity?: ActivityGranularity } = {}) =>
    axios({
      method: EMethod.get,
      url: `${base}/module-usage`,
      query,
    });

const getModuleDetail =
  (axios: any) => (query: ActivityFilters & { module: string; days?: number }) =>
    axios({
      method: EMethod.get,
      url: `${base}/module-detail`,
      query,
    });

const getPageUsage =
  (axios: any) =>
  (query: ActivityFilters & { days?: number; module?: string } = {}) =>
    axios({
      method: EMethod.get,
      url: `${base}/page-usage`,
      query,
    });

const getPageTargets = (axios: any) => (query: ActivityFilters & { page: string; days?: number }) =>
  axios({
    method: EMethod.get,
    url: `${base}/page-targets`,
    query,
  });

const listSessions =
  (axios: any) =>
  (
    query: ActivityFilters & {
      firmId?: string;
      search?: string;
      status?: "active" | "revoked" | "expired" | "all";
      clientType?: "human" | "api" | "all";
      page?: number;
      limit?: number;
      days?: number;
    },
  ) =>
    axios({
      method: EMethod.get,
      url: `${base}/list`,
      query,
    });

const getFirmBreakdown =
  (axios: any) =>
  (query: { firmId: string; days?: number; provider?: ActivityProvider; withUsers?: boolean }) =>
    axios({
      method: EMethod.get,
      url: `${base}/firm-breakdown`,
      query,
    });

const revokeSession = (axios: any) => (sessionId: string, firmId: string) =>
  axios({
    method: EMethod.put,
    url: `${base}/${sessionId}/revoke`,
    data: { firmId },
  });

const revokeAllUserSessions = (axios: any) => (userId: string, firmId: string) =>
  axios({
    method: EMethod.put,
    url: `${base}/user/${userId}/revoke-all`,
    data: { firmId },
  });

export default (axios: AxiosInstance) => ({
  listFirms: listFirms(useAxios(axios)),
  getOverview: getOverview(useAxios(axios)),
  getAnalytics: getAnalytics(useAxios(axios)),
  getWeeklyAdoption: getWeeklyAdoption(useAxios(axios)),
  getModuleUsage: getModuleUsage(useAxios(axios)),
  getModuleDetail: getModuleDetail(useAxios(axios)),
  getPageUsage: getPageUsage(useAxios(axios)),
  getPageTargets: getPageTargets(useAxios(axios)),
  listSessions: listSessions(useAxios(axios)),
  getFirmBreakdown: getFirmBreakdown(useAxios(axios)),
  revokeSession: revokeSession(useAxios(axios)),
  revokeAllUserSessions: revokeAllUserSessions(useAxios(axios)),
});
